import { LayoutHeader, ProductHeader, WorkspaceHeader } from '@dms/component'
import { FeedbackForm } from '@dms/component/feedback'
import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { useNumberParams, useRouter } from 'zens-router'
import { ApiError, CommentData, Draft, PhotoData, useCommentMutation, usePhotoMutation, useStore } from 'zens-store'
import { Button, Color, Radius, Spacing } from 'zens-storybook'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const FeedbackAdd = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const { message } = useStore()
  const [{ companyId, locationId, productId, workspaceId }] = useNumberParams()
  const { t } = useTranslation()
  const router = useRouter()
  const commentMutation = useCommentMutation({ companyId, locationId })
  const photoMutation = usePhotoMutation({ companyId, locationId })

  const save = async (draft: Draft<CommentData | PhotoData>) => {
    try {
      setErrors({})
      const commentData = draft.data as CommentData
      if (commentData.comment) {
        await commentMutation.create(draft as Draft<CommentData>)
      }
      const photoData = draft.data as PhotoData
      if (photoData.src) {
        await photoMutation.create(draft as Draft<PhotoData>)
      }
      message.add({
        message: t('Remark was added successfully'),
        status: 'success',
      })

      router.push({
        pathname: `/companies/${companyId}/feedback`,
        params: {
          companyId,
          locationId,
          productId,
          workspaceId,
        },
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`FEEDBACK: Feedback failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Device could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
    }
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton title breadcrumb accessory={<Button.Skeleton />} />}>
        {productId ? (
          <ProductHeader
            companyId={companyId}
            productId={productId}
            title={t('Add remark')}
            breadcrumbItems={[
              {
                title: t('Remarks'),
                href: {
                  pathname: `/companies/${companyId}/feedback`,
                  params: { companyId, locationId, productId },
                },
              },
              { title: t('Add remark') },
            ]}
          />
        ) : workspaceId ? (
          <WorkspaceHeader
            companyId={companyId}
            workspaceId={workspaceId}
            title={t('Add remark')}
            breadcrumbItems={[
              {
                title: t('Remarks'),
                href: {
                  pathname: `/companies/${companyId}/feedback`,
                  params: { companyId, locationId, workspaceId },
                },
              },
              { title: t('Add remark') },
            ]}
          />
        ) : null}
      </Suspense>
      <View style={styles.content}>
        <FeedbackForm
          companyId={companyId}
          locationId={locationId}
          productId={productId}
          workspaceId={workspaceId}
          onSave={save}
          errors={errors}
          onError={setErrors}
        />
      </View>
    </View>
  )
}

export default FeedbackAdd
